/**
 * Phase 14.5 Step 1 → Step 4 — `/degradation-profiles/new` composer page.
 *
 * Step 4 adds the Duplicate-from-source flow (refinement F):
 *   • Reads `location.state.duplicate_from` (set by the list page's
 *     per-row Duplicate button).
 *   • If present, the editor mounts with `initialValue = source`
 *     and `name` derived as `"<source.name> copy"`.
 *   • Editor's `_stripServerFields` already drops `id / tenant_id /
 *     created_by / created_at / updated_at / profile_version /
 *     is_seeded`, so duplicating a SEEDED profile yields an
 *     edit-write composer (the new profile is not seeded).
 *   • mode stays `"create"` regardless of whether the source was
 *     seeded — Save fires `degradationProfiles.create(...)`.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import DegradationProfileEditor from "@/components/degradation/DegradationProfileEditor";
import degradationProfiles from "@/api/degradationProfiles";

export default function NewDegradationProfilePage() {
    const location = useLocation();
    const [registry, setRegistry] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        degradationProfiles
            .registry()
            .then((r) => { if (!cancelled) setRegistry(r); })
            .catch((e) => { if (!cancelled) setErr(e?.message || "Failed to load registry."); });
        return () => { cancelled = true; };
    }, []);

    // Y6 — Duplicate-from-source: read the source profile out of
    // location.state and derive an initialValue with a "<name> copy"
    // tag. The editor's `_stripServerFields` strips the server-managed
    // fields (id / is_seeded / etc.) inside `defaults`.
    const dupFrom = location?.state?.duplicate_from || null;
    const initialValue = useMemo(() => {
        if (!dupFrom) return null;
        return { ...dupFrom, name: `${dupFrom.name || "Profile"} copy` };
    }, [dupFrom]);

    const subtitle = dupFrom
        ? `Duplicating from "${dupFrom.name}"${dupFrom.is_seeded ? " (seeded preset)" : ""} — edit freely; Save creates a new, owned profile.`
        : "Composer for a post-engine degradation profile. Pick a preset or configure all five sub-models manually.";

    return (
        <div className="space-y-4" data-testid="new-degradation-profile-page" data-duplicate-from-id={dupFrom?.id || ""}>
            <PageHeader title="New degradation profile" subtitle={subtitle} />

            {err && (
                <Alert variant="destructive" data-testid="new-degradation-profile-error">
                    <AlertDescription>{String(err)}</AlertDescription>
                </Alert>
            )}

            {!registry && !err && (
                <div
                    className="flex items-center gap-2 text-sm text-ux-text-3"
                    data-testid="new-degradation-profile-loading"
                >
                    <Loader2 className="size-4 animate-spin" />
                    Loading registry…
                </div>
            )}

            {registry && (
                <DegradationProfileEditor
                    mode="create"
                    registry={registry}
                    initialValue={initialValue}
                />
            )}
        </div>
    );
}
